/**
 * Admin → Settings view (E05 wire-pass, Block A).
 *
 * Thin tabbed shell that assembles the individual admin tab components into a
 * single routed view. No business logic lives here — every rule belongs to the
 * tab components and their models.
 *
 * Tab bar contract (matches the e2e selectors exactly):
 *   - each button carries role="tab" + data-testid="tab-<id>" + aria-selected;
 *   - tab list has role="tablist";
 *   - the content area carries role="tabpanel";
 *   - default active tab: "users".
 *
 * Tab order & icons mirror the legacy v1 admin tab bar (FP-10 fidelity):
 *   Users / Address Book / Position Types / Questionnaire / Sourcing Teams /
 *   Teams — with the net-new tabs (Research, Model Matrix) kept at the end
 *   (PM decision). Every tab carries a Font Awesome 6 icon.
 *
 * FP-10 also RE-SPLIT Sourcing Teams back into its OWN tab (reverting the earlier
 * consolidation into a single "Teams" tab with two sections): the doubled section
 * headings are gone and the previously-duplicated data-testid="teams-tab" (the
 * TeamsSection wrapper collided with RecruiterTeamsTab's own root) is resolved —
 * "teams-tab" is now unique to the Recruiter Teams tab.
 *
 * Model-per-Task Matrix (docs/superpowers/plans/2026-08-30-model-per-task-matrix.md,
 * Task 15): the separate "Prompts & Templates" and "AI Providers" tabs were
 * MERGED into one "Model Matrix" tab — `ModelMatrixTab.tsx` now owns both the
 * provider cards and the per-prompt primary/fallback model picks.
 *
 * Tab ids / accessible names (drives both getByTestId + getByRole):
 *   tab-users          → "Users"                → UsersTab (data-testid="users-tab")
 *   tab-address-book   → "Address Book"         → AddressBookTab (data-testid="staff-tab")
 *   tab-position-types → "Position Types"       → PositionTypesTab (data-testid="postype-tab")
 *   tab-questionnaire  → "Questionnaire"        → QuestionnaireTab (data-testid="kickoffq-tab")
 *   tab-sourcing-teams → "Sourcing Teams"       → SourcingTeamsTab (data-testid="srcteams-tab")
 *   tab-teams          → "Teams"                → RecruiterTeamsTab (data-testid="teams-tab")
 *   tab-research       → "Research"             → ResearchSettingsTab (data-testid="research-tab")
 *   tab-do-not-source  → "Do-Not-Source"        → DnsClientsTab (data-testid="dns-tab", E07-F07)
 *   tab-model-matrix   → "Model Matrix"         → ModelMatrixTab (data-testid="model-matrix-tab")
 *   tab-jev            → "Jev"                  → JevTab (data-testid="jev-tab", E07-F12) — ADMIN ONLY:
 *                        the tab is not rendered for any other role.
 */
import { useEffect, useState } from 'react';
import { SectionErrorBoundary } from '../ui/index.js';
import { UsersTab } from './UsersTab.js';
import { AddressBookTab } from './AddressBookTab.js';
import { RecruiterTeamsTab } from './RecruiterTeamsTab.js';
import { SourcingTeamsTab } from './SourcingTeamsTab.js';
import { QuestionnaireTab } from './QuestionnaireTab.js';
import { PositionTypesTab } from './PositionTypesTab.js';
import { ResearchSettingsTab } from './ResearchSettingsTab.js';
import { ModelMatrixTab } from './ModelMatrixTab.js';
import { DnsClientsTab } from './DnsClientsTab.js';
import { JevTab } from './JevTab.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabId =
  | 'users'
  | 'address-book'
  | 'position-types'
  | 'questionnaire'
  | 'sourcing-teams'
  | 'teams'
  | 'research'
  | 'do-not-source'
  | 'model-matrix'
  | 'jev';

interface TabDef {
  readonly id: TabId;
  readonly label: string;
  readonly testId: string;
  readonly panelTestId: string;
  /** Font Awesome 6 icon class (e.g. `fa-users`) shown before the label. */
  readonly icon: string;
  /** Shown only to role admin (absent ⇒ every role sees the tab). */
  readonly adminOnly?: boolean;
}

// Order mirrors the legacy v1 admin tab bar (FP-10); Research + AI Providers are
// net-new tabs kept at the end (PM decision). Icons are Font Awesome 6 solid.
const TABS: readonly TabDef[] = [
  {
    id: 'users',
    label: 'Users',
    testId: 'tab-users',
    panelTestId: 'panel-users',
    icon: 'fa-users',
  },
  {
    id: 'address-book',
    label: 'Address Book',
    testId: 'tab-address-book',
    panelTestId: 'panel-address-book',
    icon: 'fa-address-book',
  },
  {
    id: 'position-types',
    label: 'Position Types',
    testId: 'tab-position-types',
    panelTestId: 'panel-position-types',
    icon: 'fa-briefcase',
  },
  {
    id: 'questionnaire',
    label: 'Questionnaire',
    testId: 'tab-questionnaire',
    panelTestId: 'panel-questionnaire',
    icon: 'fa-list-check',
  },
  {
    id: 'sourcing-teams',
    label: 'Sourcing Teams',
    testId: 'tab-sourcing-teams',
    panelTestId: 'panel-sourcing-teams',
    icon: 'fa-people-group',
  },
  {
    id: 'teams',
    label: 'Teams',
    testId: 'tab-teams',
    panelTestId: 'panel-teams',
    icon: 'fa-user-group',
  },
  {
    id: 'research',
    label: 'Research',
    testId: 'tab-research',
    panelTestId: 'panel-research',
    icon: 'fa-flask',
  },
  {
    // E07-F07 — the global Do-Not-Source list (EER clients excluded from research).
    id: 'do-not-source',
    label: 'Do-Not-Source',
    testId: 'tab-do-not-source',
    panelTestId: 'panel-do-not-source',
    icon: 'fa-building-shield',
  },
  {
    id: 'model-matrix',
    label: 'Model Matrix',
    testId: 'tab-model-matrix',
    panelTestId: 'panel-model-matrix',
    icon: 'fa-robot',
  },
  {
    // E07-F12 — the Jev block of the AI settings, next to Model Matrix. Admin only.
    id: 'jev',
    label: 'Jev',
    testId: 'tab-jev',
    panelTestId: 'panel-jev',
    icon: 'fa-scale-balanced',
    adminOnly: true,
  },
];

// ---------------------------------------------------------------------------
// ResearchSection — wraps ResearchSettingsTab with a stable outer testid.
// ---------------------------------------------------------------------------

function ResearchSection(): React.JSX.Element {
  return (
    <div data-testid="research-tab">
      <ResearchSettingsTab />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SettingsView
// ---------------------------------------------------------------------------

export interface SettingsViewProps {
  /** The signed-in user's id (drives the (you) / Delete guard in UsersTab). */
  readonly currentUserId?: string | null;
  /**
   * The signed-in user's role. Tabs readable by every role (Do-Not-Source) render
   * read-only for a non-admin; the route itself is not hard-guarded (see routes.ts).
   */
  readonly currentUserRole?: string | null;
  /**
   * E06-F04-T12 — reports the active tab's label (on mount and on every switch)
   * so the shell header can read "Settings — <tab>" instead of a fixed "Users".
   */
  readonly onActiveTabChange?: (label: string) => void;
}

/**
 * Tabbed Settings view assembled from the individual admin tab components.
 * The tab bar uses role="tablist" / role="tab" so both data-testid selectors
 * (used by most e2e helpers) and getByRole('tab', { name }) selectors (used as
 * fallbacks in the same specs) resolve to the same element.
 */
export function SettingsView({
  currentUserId,
  currentUserRole,
  onActiveTabChange,
}: SettingsViewProps): React.JSX.Element {
  const [active, setActive] = useState<TabId>('users');
  const isAdmin = currentUserRole === 'admin';
  const tabs = TABS.filter((t) => !t.adminOnly || isAdmin);

  useEffect(() => {
    const label = TABS.find((t) => t.id === active)?.label;
    if (label) onActiveTabChange?.(label);
  }, [active, onActiveTabChange]);

  return (
    <div data-testid="settings-view" className="flex flex-col gap-0">
      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Settings sections"
        className="flex flex-wrap gap-0 border-b border-border bg-white"
      >
        {tabs.map((tab) => {
          const isActive = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-testid={tab.testId}
              aria-selected={isActive}
              aria-controls={tab.panelTestId}
              onClick={() => setActive(tab.id)}
              className={[
                'flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
                isActive
                  ? 'border-b-2 border-primary text-primary'
                  : 'border-b-2 border-transparent text-text-muted hover:text-text-strong',
              ].join(' ')}
            >
              <i className={`fa-solid ${tab.icon} text-xs`} aria-hidden />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab panels — each guarded by a section boundary so a query/parse crash
          in one tab degrades to an inline message instead of white-screening the
          whole shell. resetKey = active tab → switching tabs recovers a crash. */}
      <div className="mt-6">
        {active === 'users' ? (
          <div role="tabpanel" id="panel-users" aria-labelledby="tab-users">
            <SectionErrorBoundary label="Users" resetKey={active}>
              <UsersTab currentUserId={currentUserId} />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'address-book' ? (
          <div role="tabpanel" id="panel-address-book" aria-labelledby="tab-address-book">
            <SectionErrorBoundary label="Address Book" resetKey={active}>
              <AddressBookTab />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'position-types' ? (
          <div role="tabpanel" id="panel-position-types" aria-labelledby="tab-position-types">
            <SectionErrorBoundary label="Position Types" resetKey={active}>
              <PositionTypesTab />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'questionnaire' ? (
          <div role="tabpanel" id="panel-questionnaire" aria-labelledby="tab-questionnaire">
            <SectionErrorBoundary label="Questionnaire" resetKey={active}>
              <QuestionnaireTab />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'sourcing-teams' ? (
          <div role="tabpanel" id="panel-sourcing-teams" aria-labelledby="tab-sourcing-teams">
            <SectionErrorBoundary label="Sourcing Teams" resetKey={active}>
              <SourcingTeamsTab />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'teams' ? (
          <div role="tabpanel" id="panel-teams" aria-labelledby="tab-teams">
            <SectionErrorBoundary label="Teams" resetKey={active}>
              <RecruiterTeamsTab />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'research' ? (
          <div role="tabpanel" id="panel-research" aria-labelledby="tab-research">
            <SectionErrorBoundary label="Research" resetKey={active}>
              <ResearchSection />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'do-not-source' ? (
          <div role="tabpanel" id="panel-do-not-source" aria-labelledby="tab-do-not-source">
            <SectionErrorBoundary label="Do-Not-Source" resetKey={active}>
              <DnsClientsTab canEdit={currentUserRole === 'admin'} />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'model-matrix' ? (
          <div role="tabpanel" id="panel-model-matrix" aria-labelledby="tab-model-matrix">
            <SectionErrorBoundary label="Model Matrix" resetKey={active}>
              <ModelMatrixTab />
            </SectionErrorBoundary>
          </div>
        ) : null}

        {active === 'jev' && isAdmin ? (
          <div role="tabpanel" id="panel-jev" aria-labelledby="tab-jev">
            <SectionErrorBoundary label="Jev" resetKey={active}>
              <JevTab />
            </SectionErrorBoundary>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SettingsView;
